import type { Spec } from '@json-render/react';
import { buildSpecFromParts, JSONUIProvider, Renderer } from '@json-render/react';
import compact from 'lodash/compact';
import difference from 'lodash/difference';
import flatMap from 'lodash/flatMap';
import isObject from 'lodash/isObject';
import keys from 'lodash/keys';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import split from 'lodash/split';
import trim from 'lodash/trim';
import values from 'lodash/values';
import React, { useEffect, useState } from 'react';
import { captureArtifactError } from 'sentry/artifact';

import { registry } from './registry';

type SpecDashboardProps =
  | { patches: object[]; url?: never; baseUrl?: string }
  | { url: string; patches?: never; baseUrl?: never };

/**
 * Renders a json-render dashboard spec.
 *
 * Three modes:
 * - Streaming: receives `patches` array from data-spec message parts,
 *   builds spec progressively via buildSpecFromParts.
 * - URL: fetches a .spec.jsonl file from S3, parses all patches,
 *   and renders the complete spec (used for chat history / artifact viewer).
 * - Streaming + baseUrl: fetches the base spec from `baseUrl` (previous turn),
 *   then applies `patches` (current turn's delta) on top. Used when a follow-up
 *   turn emits partial spec patches that modify an existing dashboard.
 */
export const SpecDashboard = ({ patches, url, baseUrl }: SpecDashboardProps) => {
  const [fetchedSpec, setFetchedSpec] = useState<Spec | null>(null);
  const [basePatches, setBasePatches] = useState<object[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // URL mode: fetch and parse the JSONL file
  useEffect(() => {
    if (!url) return;
    let cancelled = false;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch spec: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        // Parse JSONL lines into data-spec parts for buildSpecFromParts.
        // On parse failure, stamp the line number on the error so .catch can
        // forward it to Sentry as a structured extra.
        const lines = compact(map(split(text, '\n'), trim));
        const parts = map(lines, (line, idx) => {
          try {
            return {
              type: 'data-spec' as const,
              data: { type: 'patch' as const, patch: JSON.parse(line) },
            };
          } catch (err) {
            (err as { lineNumber?: number }).lineNumber = idx + 1;
            throw err;
          }
        });
        const spec = buildSpecFromParts(parts);
        setFetchedSpec(spec);
      })
      .catch((err) => {
        if (cancelled) return;
        const lineNumber = (err as { lineNumber?: number }).lineNumber;
        captureArtifactError(err, {
          url,
          kind: 'specstream',
          extra: lineNumber !== undefined ? { line_number: lineNumber } : undefined,
        });
        setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Fetch base spec patches from a previous turn's persisted spec file.
  // These are merged with the current turn's streaming patches.
  useEffect(() => {
    if (!baseUrl) {
      setBasePatches(null);
      return;
    }
    let cancelled = false;

    fetch(baseUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch base spec: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const lines = compact(map(split(text, '\n'), trim));
        setBasePatches(map(lines, (line, idx) => {
          try {
            return JSON.parse(line);
          } catch (err) {
            (err as { lineNumber?: number }).lineNumber = idx + 1;
            throw err;
          }
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        // If base fetch fails, proceed without it — the streaming patches
        // may still produce a usable (partial) spec. Still report so we
        // know base specs are failing.
        const lineNumber = (err as { lineNumber?: number }).lineNumber;
        captureArtifactError(err, {
          url: baseUrl,
          kind: 'specstream',
          extra: {
            fetch_kind: 'base',
            ...(lineNumber !== undefined ? { line_number: lineNumber } : {}),
          },
        });
        setBasePatches(null);
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  // Streaming mode: build spec from patches (with optional base)
  const streamingSpec = patches
    ? buildSpecFromParts(
      map([...(basePatches || []), ...patches], (patch) => ({
        type: 'data-spec' as const,
        data: { type: 'patch' as const, patch },
      })),
    )
    : null;

  const rawSpec = streamingSpec ?? fetchedSpec;

  // Auto-fix: normalize and recover from common LLM-emitted spec mistakes.
  const spec = React.useMemo(() => {
    if (!rawSpec) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const s = rawSpec as any;
    if (!s.elements) return rawSpec;

    // Normalize elements that have flat properties instead of a `props` wrapper.
    // ElementRenderer calls Object.entries(element.props) and crashes if props
    // is undefined, so wrap any non-reserved keys under props.
    const reservedTopLevel = ['type', 'props', 'children', 'visible', 'on', 'repeat', 'watch'];
    const normalizedElements = mapValues(s.elements, (rawEl: unknown) => {
      if (!isObject(rawEl)) return rawEl;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const el = rawEl as any;
      const flatProps = omit(el, reservedTopLevel);
      const hasFlatProps = keys(flatProps).length > 0;
      if (!hasFlatProps && el.props) return el;
      const reserved = pick(el, reservedTopLevel);
      return {
        ...reserved,
        // Flat keys take precedence — if both `props.title` and a top-level
        // `title` exist, the LLM clearly meant the flat one to stick.
        props: { ...(el.props || {}), ...flatProps },
      };
    });
    const normalized = normalizedElements === s.elements ? s : { ...s, elements: normalizedElements };

    // Auto-root: if the spec has elements but no root, try to recover.
    // First, prefer an element literally keyed "root" — LLMs often name
    // the container element "root" but forget to emit the top-level pointer.
    // Otherwise, synthesize a Stack wrapper from orphan elements
    // (elements not referenced as a child).
    if (normalized.root) return normalized as Spec;

    const allKeys = keys(normalized.elements);
    if (allKeys.length === 0) return normalized as Spec;

    if (normalized.elements.root) {
      return { ...normalized, root: 'root' } as Spec;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const referencedChildren = flatMap(values(normalized.elements), (el: any) => el.children || []);
    const orphans = difference(allKeys, referencedChildren);
    if (orphans.length === 0) return normalized as Spec;

    return {
      ...normalized,
      root: '__auto_root__',
      elements: {
        ...normalized.elements,
        __auto_root__: { type: 'Stack', props: { direction: 'vertical', gap: 'sm' }, children: orphans },
      },
    } as Spec;
  }, [rawSpec]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
        Failed to load dashboard: {error}
      </div>
    );
  }

  if (!spec) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
        Loading dashboard...
      </div>
    );
  }

  // Extract initial state from the spec for interactive controls ($bindState)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialState = (spec as any).state as Record<string, unknown> | undefined;

  return (
    <JSONUIProvider registry={registry} initialState={initialState}>
      <Renderer spec={spec} registry={registry} loading={!!patches} />
    </JSONUIProvider>
  );
};
